/**
 * Auto-TOC Generation Hook
 * Automatically triggers TOC generation when appropriate and provides manual controls
 */

import { useState, useEffect, useCallback } from 'react';
import { ProjectState } from '../types/project';
import { Chapter } from '../types';
import { 
  generateEnhancedTableOfContents, 
  applyTOCToProject, 
  EnhancedTableOfContents,
  TOC_STYLES
} from '../utils/smartTableOfContents';

interface TOCGenerationState {
  enhancedTOC: EnhancedTableOfContents | null;
  isGenerating: boolean;
  lastGeneratedAt: Date | null;
  autoGenerated: boolean;
  error: string | null;
}

interface TOCGenerationConfig {
  autoTriggerEnabled: boolean;
  minChapters: number;
  debounceMs: number;
  preferredStyle: string;
  includeEmptySections: boolean;
}

interface UseTOCGenerationReturn {
  // State
  tocState: TOCGenerationState;
  config: TOCGenerationConfig;
  
  // Actions
  generateTOC: (manual?: boolean) => Promise<void>;
  applyTOC: (style?: string) => void;
  regenerateTOC: () => Promise<void>;
  
  // Configuration
  updateConfig: (newConfig: Partial<TOCGenerationConfig>) => void;
  
  // Utilities
  shouldAutoGenerate: (projectState: ProjectState) => boolean;
  getTOCCompletionStatus: (projectState: ProjectState) => {
    hasContent: boolean;
    isOutdated: boolean;
    qualityScore: number;
  };
}

const defaultConfig: TOCGenerationConfig = {
  autoTriggerEnabled: true,
  minChapters: 2,
  debounceMs: 2000,
  preferredStyle: 'standard',
  includeEmptySections: false,
};

export const useTOCGeneration = (
  projectState: ProjectState,
  onProjectUpdate: (updates: Partial<ProjectState>) => void,
  initialConfig?: Partial<TOCGenerationConfig>
): UseTOCGenerationReturn => {
  const [tocState, setTocState] = useState<TOCGenerationState>({
    enhancedTOC: null,
    isGenerating: false,
    lastGeneratedAt: null,
    autoGenerated: false,
    error: null,
  });

  const [config, setConfig] = useState<TOCGenerationConfig>({
    ...defaultConfig,
    ...initialConfig,
  });

  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);

  // Check if TOC should be auto-generated
  const shouldAutoGenerate = useCallback((state: ProjectState): boolean => {
    if (!config.autoTriggerEnabled) return false;
    
    // Check if we have enough chapters
    const chapterCount = state.chapters?.length || 0;
    if (chapterCount < config.minChapters) return false;
    
    // Check if TOC is missing or outdated
    const hasTOC = Boolean(state.frontMatter?.tableOfContents?.trim());
    if (!hasTOC) return true;
    
    // Check if chapters have been modified since last TOC generation
    if (tocState.lastGeneratedAt) {
      const chaptersModified = state.chapters?.some(chapter => {
        const chapterModified = new Date(chapter.updatedAt || 0);
        return chapterModified > tocState.lastGeneratedAt!;
      });
      
      if (chaptersModified) return true;
    }
    
    return false;
  }, [config, tocState.lastGeneratedAt]);

  // Get TOC completion status and quality assessment
  const getTOCCompletionStatus = useCallback((state: ProjectState) => {
    const hasContent = Boolean(state.frontMatter?.tableOfContents?.trim());
    
    let isOutdated = false;
    if (tocState.lastGeneratedAt && state.chapters) {
      isOutdated = state.chapters.some(chapter => {
        const chapterModified = new Date(chapter.updatedAt || 0);
        return chapterModified > tocState.lastGeneratedAt!;
      });
    }
    
    // Calculate quality score based on content completeness and structure
    let qualityScore = 0;
    if (hasContent) {
      qualityScore += 40; // Base score for having content
      
      const tocContent = state.frontMatter?.tableOfContents || '';
      const chapterCount = state.chapters?.length || 0;
      const tocLines = tocContent.split('\n').filter(line => line.trim());
      
      // Check if TOC matches chapter count reasonably
      if (tocLines.length >= chapterCount * 0.8) qualityScore += 30;
      
      // Check for proper formatting (page numbers, proper structure)
      if (tocContent.includes('.') && /\d+/.test(tocContent)) qualityScore += 20;
      
      // Check for professional formatting
      if (tocContent.includes('Chapter') && /\.\.\.*/.test(tocContent)) qualityScore += 10;
    }
    
    return {
      hasContent,
      isOutdated,
      qualityScore: Math.min(100, qualityScore),
    };
  }, [tocState.lastGeneratedAt]);

  // Generate TOC function
  const generateTOC = useCallback(async (manual: boolean = false): Promise<void> => {
    if (tocState.isGenerating) return;
    
    setTocState(prev => ({
      ...prev,
      isGenerating: true,
      error: null,
    }));

    try {
      // Add small delay for user feedback
      if (manual) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      const enhancedTOC = generateEnhancedTableOfContents(projectState);
      
      setTocState(prev => ({
        ...prev,
        enhancedTOC,
        isGenerating: false,
        lastGeneratedAt: new Date(),
        autoGenerated: !manual,
        error: null,
      }));
      
    } catch (error: any) {
      console.error('❌ TOC generation failed:', error);
      
      setTocState(prev => ({
        ...prev,
        isGenerating: false,
        error: error.message || 'TOC generation failed',
      }));
    }
  }, [projectState, tocState.isGenerating]);

  // Apply generated TOC to project
  const applyTOC = useCallback((style?: string): void => {
    if (!tocState.enhancedTOC) {
      console.warn('No TOC generated to apply');
      return;
    }
    
    const styleToUse = style || config.preferredStyle;
    const projectUpdates = applyTOCToProject(projectState, tocState.enhancedTOC, styleToUse);
    
    onProjectUpdate(projectUpdates);
    
  }, [tocState.enhancedTOC, config.preferredStyle, projectState, onProjectUpdate]);

  // Regenerate TOC (force generation regardless of conditions)
  const regenerateTOC = useCallback(async (): Promise<void> => {
    await generateTOC(true);
  }, [generateTOC]);

  // Update configuration
  const updateConfig = useCallback((newConfig: Partial<TOCGenerationConfig>): void => {
    setConfig(prev => ({ ...prev, ...newConfig }));
  }, []);

  // Auto-generation effect with debouncing
  useEffect(() => {
    if (!config.autoTriggerEnabled) return;
    
    // Clear existing timer
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    
    // Set new timer for debounced auto-generation
    const timer = setTimeout(() => {
      if (shouldAutoGenerate(projectState)) {
        generateTOC(false); // Auto-generated
      }
    }, config.debounceMs);
    
    setDebounceTimer(timer);
    
    // Cleanup
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [
    projectState.chapters, 
    projectState.frontMatter?.tableOfContents,
    config.autoTriggerEnabled,
    config.debounceMs,
    shouldAutoGenerate,
    generateTOC
  ]);

  // Auto-apply TOC after batch generation completion
  useEffect(() => {
    // Check if we just completed a batch generation and don't have a TOC
    const batchGenerationComplete = Boolean(
      projectState.frontMatter?.dedication ||
      projectState.frontMatter?.prologue ||
      projectState.backMatter?.epilogue
    );
    
    const needsTOC = !projectState.frontMatter?.tableOfContents?.trim();
    
    if (batchGenerationComplete && needsTOC && tocState.enhancedTOC && !tocState.isGenerating) {
      applyTOC();
    }
  }, [
    projectState.frontMatter?.dedication,
    projectState.frontMatter?.prologue,
    projectState.backMatter?.epilogue,
    projectState.frontMatter?.tableOfContents,
    tocState.enhancedTOC,
    tocState.isGenerating,
    applyTOC
  ]);

  return {
    tocState,
    config,
    generateTOC,
    applyTOC,
    regenerateTOC,
    updateConfig,
    shouldAutoGenerate,
    getTOCCompletionStatus,
  };
};

// Utility hook for TOC status monitoring
export const useTOCStatus = (projectState: ProjectState) => {
  const hasChapters = Boolean(projectState.chapters?.length);
  const hasEnoughChapters = (projectState.chapters?.length || 0) >= 2;
  const hasTOC = Boolean(projectState.frontMatter?.tableOfContents?.trim());
  
  const status = !hasChapters 
    ? 'no-chapters'
    : !hasEnoughChapters 
    ? 'insufficient-chapters'
    : !hasTOC 
    ? 'missing-toc'
    : 'complete';
    
  const recommendation = {
    'no-chapters': 'Create chapters first before generating TOC',
    'insufficient-chapters': 'Add more chapters for a meaningful TOC',
    'missing-toc': 'Generate TOC automatically from your chapters',
    'complete': 'TOC is up to date',
  }[status];
  
  return {
    status,
    hasChapters,
    hasEnoughChapters,
    hasTOC,
    recommendation,
  };
};

export default useTOCGeneration;